/**
 * Path-based visibility for the widget.
 *
 * Patterns support a `*` wildcard that matches any run of characters within a
 * single path. They are matched against `window.location.pathname`.
 *
 * Examples:
 *   /checkout       → matches /checkout (exact)
 *   /checkout/*     → matches /checkout/anything
 *   /admin/*        → matches /admin, /admin/foo, /admin/foo/bar
 *   /api/v1/*       → matches /api/v1/users, etc.
 *   *               → matches any path
 *
 * Hide takes precedence over show: if the path matches a hide pattern, the
 * widget is hidden even if it also matches a show pattern.
 *
 * For SPAs, we listen to popstate AND patch pushState/replaceState so that
 * client-side navigations re-evaluate visibility. We never fire on the initial
 * page load — the widget owner runs evaluate() once at boot.
 */

function patternToRegex(pattern: string): RegExp {
  // Escape regex special chars except `*`, then turn `*` into `.*`
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp('^' + escaped + '$');
}

export function pathMatches(path: string, patterns: string[]): boolean {
  if (patterns.length === 0) return false;
  return patterns.some((p) => patternToRegex(p).test(path));
}

/** Returns true if the widget should be visible at the current pathname. */
export function shouldShowAt(pathname: string, hidePatterns: string[], showPatterns: string[]): boolean {
  if (pathMatches(pathname, hidePatterns)) return false;
  if (showPatterns.length > 0 && !pathMatches(pathname, showPatterns)) return false;
  return true;
}

type RouteListener = () => void;

let listenerInstalled = false;
const listeners = new Set<RouteListener>();

function emit(): void {
  listeners.forEach((fn) => {
    try { fn(); } catch (e) { console.error('[Klientele] visibility listener threw', e); }
  });
}

/**
 * Install global hooks that fire `emit()` whenever the URL changes — covers
 * back/forward navigation AND programmatic SPA navigation via pushState/replaceState.
 * Idempotent.
 */
export function installRouteHooks(): void {
  if (listenerInstalled) return;
  listenerInstalled = true;

  const origPushState = history.pushState.bind(history);
  const origReplaceState = history.replaceState.bind(history);

  history.pushState = function (...args: Parameters<History['pushState']>) {
    const ret = origPushState(...args);
    queueMicrotask(emit);
    return ret;
  };
  history.replaceState = function (...args: Parameters<History['replaceState']>) {
    const ret = origReplaceState(...args);
    queueMicrotask(emit);
    return ret;
  };

  window.addEventListener('popstate', () => emit());
  window.addEventListener('hashchange', () => emit());
}

export function onRouteChange(fn: RouteListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
